/**
 * Tour Error Handler
 * Centralized error handling for tour operations
 */

import { TpiTourKey } from '@typeRoots/common/tourProgress';
import { logger } from 'src/helpers/logger';

export type TourErrorCode =
  | 'TOUR_START_FAILED'
  | 'TOUR_SAVE_FAILED'
  | 'TOUR_ELEMENT_NOT_FOUND'
  | 'TOUR_NETWORK_ERROR'
  | 'TOUR_RETRY_EXHAUSTED';

export interface TourError {
  code: TourErrorCode;
  tourKey: TpiTourKey;
  originalError: unknown;
  userMessage: string;
  shouldNotifyUser: boolean;
}

/**
 * Handle tour errors consistently across the application
 * Logs errors and optionally notifies the user
 */
export function handleTourError(error: TourError): void {
  // Always log the error
  logger.error(`Tour error [${error.code}] for "${error.tourKey}":`, {
    code: error.code,
    tourKey: error.tourKey,
    error: error.originalError,
    userMessage: error.userMessage,
  });

  // User notification is handled by the caller using Toast system
  // This keeps the error handler decoupled from UI concerns
}

/**
 * Create a standardized tour error object
 */
export function createTourError(
  code: TourErrorCode,
  tourKey: TpiTourKey,
  originalError: unknown,
  options: {
    userMessage?: string;
    shouldNotifyUser?: boolean;
  } = {},
): TourError {
  const defaultMessages: Record<TourErrorCode, string> = {
    TOUR_START_FAILED: 'Unable to start the tour. Please refresh and try again.',
    TOUR_SAVE_FAILED: 'Unable to save your progress. Your tour will restart next time.',
    TOUR_ELEMENT_NOT_FOUND: 'Tour could not locate required elements on the page.',
    TOUR_NETWORK_ERROR: 'Network error while saving tour progress. Please check your connection.',
    TOUR_RETRY_EXHAUSTED: 'Failed to save tour progress after multiple attempts.',
  };

  return {
    code,
    tourKey,
    originalError,
    userMessage: options.userMessage ?? defaultMessages[code],
    shouldNotifyUser: options.shouldNotifyUser ?? true,
  };
}
